import { useState, useEffect, type ReactNode } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/contexts/AuthContext";
import {
  updateName,
  updateEmail,
  sendDeleteAccountRequest,
  sendFeedback,
} from "@/lib/api/api";
import { getAllWorkspaces } from "@/lib/api/workspaces";
import { buyTopUp, createAgentsCheckout } from "@/lib/api/billing";
import { track } from "@/lib/utils/track";
import { generateSlugId } from "@/lib/routing/slug-id";
import { toast } from "sonner";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft01Icon, GlobeIcon, GridIcon, UserIcon } from "@hugeicons/core-free-icons";
import {
  settingsButtonClass,
  settingsDangerOutlineClass,
  settingsSelectTriggerClass,
} from "@/components/shared/settings/SettingsKit";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { cn } from "@/lib/utils";
import CancelFlowDialog from "@/components/billing/CancelFlowDialog";
import AutoTopUpRows from "@/components/billing/AutoTopUpRows";
import { useAutoTopUp } from "@/hooks/useAutoTopUp";
import {
  CANCELLATION_REASONS,
  formatCancellationReason,
  type CancellationReason,
} from "@/lib/constants/cancellationReasons";
import HumblyticsLogo from "@/assets/humblytics-web-app-logo.svg";
import { ThemeSwitch } from "@/components/shared/ThemeSwitch";
import { AMIR_CALL_URL } from "@/lib/constants/support";

const SUPPORT_EMAIL = "support@humblytics.com";

type UserData = {
  name: string;
  email: string;
};

const decodeAuthToken = (token: string): UserData => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return { name: "--", email: "" };
  }
};

function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(Math.round(n));
}

/** Paper "Settings · Account": raised card with a title row over hairline-divided rows. */
function AccountCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="surface-raised flex flex-col overflow-clip">
      <div className="flex flex-col gap-[3px] border-b border-border px-5 py-[18px]">
        <h2 className="text-sm font-semibold leading-[18px] text-foreground">{title}</h2>
        <p className="text-xs leading-4 text-foreground/60">{description}</p>
      </div>
      {children}
    </section>
  );
}

function ProfileField({
  id,
  label,
  hint,
  type,
  value,
  placeholder,
  onChange,
  onSave,
  dirty,
  saving,
}: {
  id: string;
  label: string;
  hint: string;
  type: "text" | "email";
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onSave: () => void;
  dirty: boolean;
  saving: boolean;
}) {
  return (
    <form
      className="flex flex-col gap-3 px-5 py-[18px] sm:flex-row sm:items-end [&+&]:border-t [&+&]:border-border"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
        <label htmlFor={id} className="text-xs font-medium leading-4 text-foreground">
          {label}
        </label>
        <p className="text-xs leading-4 text-foreground/60">{hint}</p>
        <Input
          id={id}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 rounded-lg bg-background px-3 text-sm shadow-none"
        />
      </div>
      <Button
        type="submit"
        variant={dirty ? "outline" : "secondary"}
        disabled={!dirty || saving}
        className={cn(settingsButtonClass, "mb-px min-w-[76px] disabled:opacity-100", !dirty && "text-foreground/45")}
      >
        {saving ? (
          <>
            <Spinner className="size-3.5" />
            Updating
          </>
        ) : (
          "Update"
        )}
      </Button>
    </form>
  );
}

export default function UserSettings() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    planName,
    isFreePlan,
    hasActiveTrial,
    trialDaysLeft,
    hasActiveSubscription,
    isReadOnly,
    eventLimit,
    eventsUsed,
    eventUsagePercent,
    handleUpgrade,
    openUpdatePortal,
    isAgentPlan,
    planKind,
  } = useSubscription();
  const {
    autoTopUp,
    topUps,
    dataPoints,
    refresh: refreshDataPoints,
    save: saveAutoTopUp,
    saving: savingAutoTopUp,
  } = useAutoTopUp();
  const [agentActionBusy, setAgentActionBusy] = useState(false);

  const { data: workspaces = [] } = useQuery({
    queryKey: ["workspaces", token],
    queryFn: () => (token ? getAllWorkspaces(token) : []),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });
  const firstWorkspace = workspaces[0];
  const workspaceBase = firstWorkspace
    ? `/workspace/${generateSlugId(firstWorkspace.name || "workspace", firstWorkspace.workspaceId)}`
    : null;

  useEffect(() => {
    document.title = "Account settings - Humblytics";
  }, []);

  const [userName, setUserName] = useState("--");
  const [email, setEmail] = useState("");
  const [newUserName, setNewUserName] = useState("--");
  const [newEmail, setNewEmail] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);
  const [isSavingEmail, setIsSavingEmail] = useState(false);

  const [selectedDeleteReason, setSelectedDeleteReason] = useState<
    CancellationReason | ""
  >("");
  const [deleteAccountReasonOther, setDeleteAccountReasonOther] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [cancelFlowOpen, setCancelFlowOpen] = useState(false);
  const [cancelFlowMode, setCancelFlowMode] = useState<"cancel" | "delete">(
    "cancel",
  );

  useEffect(() => {
    if (!token) return;
    const userData = decodeAuthToken(token);
    // Agent and MCP sign-ups have no name, so the backend stores the email's local part. Show the empty
    // field with its placeholder instead of "am+2093093" as if it were a name.
    const localPart = (userData.email || "").split("@")[0];
    const name = userData.name && userData.name !== "--" && userData.name !== localPart ? userData.name : "";
    setUserName(name);
    setEmail(userData.email || "");
    setNewUserName(name);
    setNewEmail(userData.email || "");
    if (searchParams.get("change_email") !== "true") return;
    // The app's <Toaster> mounts after this page and subscribes in its own
    // effect; a toast fired in the same commit is dropped, so wait a tick.
    const id = window.setTimeout(() => toast.success("Email updated"), 0);
    return () => window.clearTimeout(id);
  }, [token, searchParams]);

  const saveName = async () => {
    if (!token || userName === newUserName) return;
    try {
      setIsSavingName(true);
      await updateName(newUserName, token);
      setUserName(newUserName);
      toast.success("Name updated");
    } catch (error) {
      console.error("Failed to update name:", error);
      toast.error("Couldn't update your name", { description: "Try again in a moment." });
    } finally {
      setIsSavingName(false);
    }
  };

  const saveEmail = async () => {
    if (!token || email === newEmail) return;
    try {
      setIsSavingEmail(true);
      await updateEmail(newEmail, token);
      setEmail(newEmail);
      toast.success("Check your inbox", {
        description: `We sent a confirmation link to ${newEmail}.`,
      });
    } catch (error) {
      console.error("Failed to update email:", error);
      toast.error("Couldn't update your email", { description: "Try again in a moment." });
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!token) return;
    try {
      setIsDeleting(true);
      const finalReason = formatCancellationReason(
        selectedDeleteReason,
        deleteAccountReasonOther,
      );
      await sendFeedback("Account Delete Request", token);
      await sendDeleteAccountRequest(token, finalReason);
      toast.success("Deletion requested", {
        description: "Our team processes it within 48 hours.",
      });
    } catch (error) {
      console.error("Failed to send delete request:", error);
      toast.error("Couldn't send your request", { description: "Try again later." });
    } finally {
      setIsDeleting(false);
    }
  };

  // Free start and Agents are prepaid data points, not a subscription: no Cancel plan, no plan picker as
  // the main action. Free start buys the Agents plan; Agents buys a top-up.
  const onFreeStart = planKind === "free_start";
  const agentAction = !isAgentPlan
    ? null
    : onFreeStart
      ? { label: "Get 100K for $39", primary: true }
      : topUps
        ? { label: `Add ${formatCompact(topUps.blockPoints)} for $29`, primary: false }
        : null;
  const runAgentAction = async () => {
    if (!token || !isAgentPlan) return;
    setAgentActionBusy(true);
    try {
      if (onFreeStart) {
        track("agents_checkout_clicked", token, { surface: "account_settings" });
        const { url } = await createAgentsCheckout(token, {
          successPath: "/agent-complete?session_id={CHECKOUT_SESSION_ID}",
          cancelPath: "/user-settings",
        });
        window.location.assign(url);
        return;
      }
      track("agents_top_up_clicked", token, { surface: "account_settings", status: dataPoints?.status });
      const res = await buyTopUp(token, 1);
      toast.success(`Added ${formatCompact(res.points)} data points`, { description: "Valid for 12 months." });
      await refreshDataPoints();
    } catch {
      toast.error(onFreeStart ? "Couldn't open checkout" : "Couldn't add data points", {
        description: "Try again in a moment.",
      });
    }
    setAgentActionBusy(false);
  };

  const isPaid = !isFreePlan && hasActiveSubscription && !isAgentPlan;
  const agentMeter = isAgentPlan && dataPoints && dataPoints.limit > 0;
  const usagePct = agentMeter
    ? Math.min(100, (dataPoints.used / dataPoints.limit) * 100)
    : Math.min(100, Math.max(0, eventUsagePercent));
  const planMeta = [
    hasActiveTrial ? `Trial · ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left` : null,
    agentMeter
      ? `${formatCompact(dataPoints.used)} of ${formatCompact(dataPoints.limit)} data points used · ${formatCompact(dataPoints.remaining)} left`
      : eventLimit > 0
        ? `${formatCompact(eventsUsed)} of ${formatCompact(eventLimit)} data points this month`
        : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const status = isReadOnly ? "Ended" : hasActiveTrial ? "Trial" : hasActiveSubscription ? "Active" : null;

  const scopes = [
    { icon: UserIcon, label: "Account", desc: "Profile, plan and billing", to: null, current: true },
    { icon: GridIcon, label: "Workspace", desc: "Team, sites, tracking defaults", to: workspaceBase && `${workspaceBase}/settings`, current: false },
    { icon: GlobeIcon, label: "Site", desc: "Open a site to change its settings", to: null, current: false },
  ];

  const deleteTrigger = (
    <Button variant="outline" className={settingsDangerOutlineClass}>
      Delete account
    </Button>
  );

  return (
    <div className="min-h-svh bg-canvas text-foreground">
      <header className="flex h-[60px] items-center gap-2.5 px-4 sm:px-7">
        <Link to="/dashboard" className="flex flex-1 items-center gap-2.5">
          <img src={HumblyticsLogo} alt="" className="size-[26px]" />
          <span className="text-sm font-semibold leading-[18px]">Humblytics</span>
        </Link>
        <Button variant="outline" onClick={() => navigate(-1)} className={cn(settingsButtonClass, "gap-1.5 border-input bg-background")}>
          <HugeiconsIcon icon={ArrowLeft01Icon} size={14} />
          Back
        </Button>
      </header>

      <main className="flex justify-center px-4 pb-[60px] pt-6 sm:pt-9">
        <div className="flex w-full max-w-[760px] flex-col gap-[18px]">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] sm:text-[28px] sm:leading-[34px]">
              Account settings
            </h1>
            <p className="text-sm leading-[18px] text-foreground/60">
              Your profile, plan and billing, shared across every workspace and site.
            </p>
          </div>

          <nav aria-label="Settings scope" className="flex flex-col gap-1 rounded-xl bg-[#EBEBEB] p-1 sm:flex-row dark:bg-white/[0.06]">
            {scopes.map((scope) => {
              const body = (
                <>
                  <span
                    className={cn(
                      "flex size-7 shrink-0 items-center justify-center rounded-lg",
                      scope.current ? "bg-foreground text-background" : "bg-background text-foreground/70",
                    )}
                  >
                    <HugeiconsIcon icon={scope.icon} size={14} strokeWidth={2} />
                  </span>
                  <span className="flex min-w-0 flex-col gap-px text-left">
                    <span className="text-[13px] font-medium leading-4 text-foreground">{scope.label}</span>
                    <span className="truncate text-xs leading-4 text-foreground/60">{scope.desc}</span>
                  </span>
                </>
              );
              const cls = cn(
                "flex flex-1 items-center gap-2.5 rounded-lg px-3.5 py-3",
                scope.current && "surface-raised rounded-lg",
              );
              return scope.to ? (
                <Link key={scope.label} to={scope.to} className={cn(cls, "transition-colors hover:bg-background/60")}>
                  {body}
                </Link>
              ) : (
                <div
                  key={scope.label}
                  className={cn(cls, !scope.current && "opacity-60")}
                  aria-current={scope.current ? "page" : undefined}
                  aria-disabled={scope.current ? undefined : true}
                >
                  {body}
                </div>
              );
            })}
          </nav>

          <AccountCard title="Profile" description="Your personal information and email preferences">
            <ProfileField
              id="userName"
              label="Full name"
              hint="Shown across your account and in team invites."
              type="text"
              placeholder="Your full name"
              value={newUserName}
              onChange={setNewUserName}
              onSave={saveName}
              dirty={userName !== newUserName}
              saving={isSavingName}
            />
            <ProfileField
              id="email"
              label="Email address"
              hint="We send a confirmation link to verify any change."
              type="email"
              placeholder="you@company.com"
              value={newEmail}
              onChange={setNewEmail}
              onSave={saveEmail}
              dirty={email !== newEmail}
              saving={isSavingEmail}
            />
          </AccountCard>

          <AccountCard title="Appearance" description="Light, dark, or follow your device. Saved in this browser.">
            <div className="flex flex-col gap-3 px-5 py-[18px] sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-[3px]">
                <span className="text-xs font-medium leading-4">Theme</span>
                <span className="text-xs leading-4 text-foreground/60">System switches with your device's setting.</span>
              </div>
              <ThemeSwitch className="w-full sm:w-[264px]" />
            </div>
          </AccountCard>

          <AccountCard title="Plan and billing" description="Your subscription applies to your whole account, every workspace and site.">
            <div className="flex flex-wrap items-center gap-4 px-5 py-[18px]">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden>
                  <rect x="3" y="11" width="3.5" height="6" rx="1.2" className="fill-foreground/25" />
                  <rect x="8.25" y="7" width="3.5" height="10" rx="1.2" className="fill-foreground/55" />
                  <rect x="13.5" y="3" width="3.5" height="14" rx="1.2" className="fill-foreground" />
                </svg>
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold leading-5">{planName || "Free"}</span>
                  {status && (
                    <span className="flex h-5 items-center gap-[5px] rounded-md bg-muted px-[7px] text-xs font-medium leading-4">
                      <span className={cn("size-1.5 shrink-0 rounded-full", isReadOnly ? "bg-foreground/35" : "bg-live")} />
                      {status}
                    </span>
                  )}
                </div>
                {planMeta && <p className="text-xs leading-4 text-foreground/60 tabular-nums">{planMeta}</p>}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                {isAgentPlan ? (
                  <>
                    {agentAction && (
                      <Button
                        variant={agentAction.primary ? "default" : "outline"}
                        disabled={agentActionBusy}
                        onClick={() => void runAgentAction()}
                        className={cn(settingsButtonClass, !agentAction.primary && "border-input bg-background")}
                      >
                        {agentActionBusy ? <Spinner className="size-3.5" /> : null}
                        {agentAction.label}
                      </Button>
                    )}
                    <button
                      type="button"
                      onClick={() => void handleUpgrade()}
                      className="text-[11px] leading-4 text-foreground/55 transition-colors hover:text-foreground"
                    >
                      Team plans with the full app
                    </button>
                  </>
                ) : (
                <>
                <Button
                  variant={isPaid ? "outline" : "default"}
                  onClick={() => void (isPaid ? openUpdatePortal() : handleUpgrade())}
                  className={cn(settingsButtonClass, isPaid && "border-input bg-background")}
                >
                  {isPaid ? "Manage billing" : "View plans"}
                </Button>
                {hasActiveSubscription && (
                  <button
                    type="button"
                    onClick={() => {
                      setCancelFlowMode("cancel");
                      setCancelFlowOpen(true);
                    }}
                    className="text-[11px] leading-4 text-foreground/45 transition-colors hover:text-foreground/70"
                  >
                    Cancel plan
                  </button>
                )}
                </>
                )}
              </div>
            </div>
            {(agentMeter || eventLimit > 0) && (
              <div className="px-5 pb-[18px]">
                <div
                  className="h-1.5 rounded-[2px] bg-[#F2F2F2] dark:bg-white/10"
                  role="progressbar"
                  aria-label="Data points used this month"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(usagePct)}
                >
                  <div className="h-1.5 rounded-[2px] bg-foreground" style={{ width: `${usagePct}%` }} />
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-5 py-3.5">
              <span className="text-xs leading-4 text-foreground/60">Questions about your plan or an invoice?</span>
              <span className="flex items-center gap-3 text-xs font-medium leading-4">
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-foreground underline underline-offset-2 hover:text-foreground/70">
                  {SUPPORT_EMAIL}
                </a>
                <a
                  href={AMIR_CALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground/60 underline underline-offset-2 hover:text-foreground"
                >
                  Book a call
                </a>
              </span>
            </div>
          </AccountCard>

          {autoTopUp && (
            <AccountCard
              title="Top-ups"
              description={`${formatCompact(autoTopUp.blockPoints)} data points per top-up, valid 12 months.`}
            >
              <AutoTopUpRows autoTopUp={autoTopUp} save={saveAutoTopUp} saving={savingAutoTopUp} />
            </AccountCard>
          )}

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {[
              {
                title: "Team and workspaces",
                desc: "Invites and roles live under Workspace settings › Team in each workspace.",
                cta: "Open workspaces",
                to: "/workspaces",
              },
              {
                title: "API & MCP",
                desc: "Keys are managed per workspace under API & MCP.",
                cta: "Open API & MCP",
                to: workspaceBase ? `${workspaceBase}/api` : "/dashboard",
              },
            ].map((card) => (
              <section key={card.title} className="surface-raised flex flex-col gap-3 px-5 py-[18px]">
                <div className="flex flex-col gap-[3px]">
                  <h2 className="text-sm font-semibold leading-[18px]">{card.title}</h2>
                  <p className="text-xs leading-[18px] text-foreground/60">{card.desc}</p>
                </div>
                <Button asChild variant="outline" className={cn(settingsButtonClass, "w-fit border-input bg-background")}>
                  <Link to={card.to}>{card.cta}</Link>
                </Button>
              </section>
            ))}
          </div>

          <section className="flex flex-col gap-3.5 rounded-xl bg-card px-5 py-4 shadow-[0_0_0_1px_#FECDCA,0_10px_15px_-3px_rgb(0_0_0/0.05),0_4px_6px_-4px_rgb(0_0_0/0.05)] sm:flex-row sm:items-center dark:shadow-[0_0_0_1px_rgb(240_68_56/0.35)]">
            <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
              <h2 className="text-sm font-medium leading-[18px] text-danger">Delete account</h2>
              <p className="text-xs leading-4 text-foreground/60">
                Permanently delete your account and all associated data. This cannot be undone.
              </p>
            </div>
            {hasActiveSubscription ? (
              <Button
                variant="outline"
                className={settingsDangerOutlineClass}
                onClick={() => {
                  setCancelFlowMode("delete");
                  setCancelFlowOpen(true);
                }}
              >
                Delete account
              </Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>{deleteTrigger}</AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Our team processes deletion requests within 48 hours. After that, your
                      data is removed for good and can't be recovered.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="deleteReason" className="text-xs font-medium">
                      Why are you leaving? (optional)
                    </Label>
                    <Select
                      value={selectedDeleteReason}
                      onValueChange={(value) => setSelectedDeleteReason(value as CancellationReason)}
                    >
                      <SelectTrigger id="deleteReason" className={settingsSelectTriggerClass}>
                        <SelectValue placeholder="Pick a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {CANCELLATION_REASONS.map((reason) => (
                          <SelectItem key={reason.value} value={reason.value}>
                            {reason.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedDeleteReason === "other" && (
                      <Input
                        value={deleteAccountReasonOther}
                        onChange={(e) => setDeleteAccountReasonOther(e.target.value)}
                        type="text"
                        id="deleteReasonOther"
                        placeholder="Tell us more"
                        className="h-9 rounded-lg px-3 text-sm shadow-none"
                      />
                    )}
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep account</AlertDialogCancel>
                    <AlertDialogAction variant="destructive" onClick={handleDeleteAccount} disabled={isDeleting}>
                      {isDeleting ? (
                        <>
                          <Spinner className="size-3.5" />
                          Sending
                        </>
                      ) : (
                        "Request deletion"
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </section>
        </div>
      </main>
      <CancelFlowDialog
        open={cancelFlowOpen}
        onOpenChange={setCancelFlowOpen}
        mode={cancelFlowMode}
        surface={cancelFlowMode === "delete" ? "user_settings_delete" : "user_settings"}
      />
    </div>
  );
}
